import type { CodiceContatore } from '@praticheenergia/domain'
import { dec, type Dec } from './dec'
import { bloccante, type Diagnostica } from './diagnostica'
import { mesiDelPeriodo, type ContatorePeriodo, type DatiPeriodo, type MeseDelPeriodo } from './ingresso'
import type { RegolePeriodo } from './regole-periodo'
import type { Intestazione } from './tracciato'

/**
 * Un giro di quadrante e' plausibile solo se la lettura precedente era vicina
 * al fondo scala: 9990 -> 10 su quattro cifre e' un giro, 1200 -> 50 un errore.
 *
 * ponytail: soglia fissa al 90% del fondo scala. Un falso allarme costa un clic,
 * un falso silenzio manda un consumo sbagliato in dichiarazione. Se i dati reali
 * mostrano troppi falsi allarmi, derivare la soglia dal consumo mediano.
 */
const SOGLIA_FONDO_SCALA = dec('0.9')

type EsitoCalo = 'giro_di_quadrante' | 'decrescente' | 'cifre_intere_ignote'

function classificaCalo(precedente: Dec, corrente: Dec, cifreIntere: number): EsitoCalo {
  if (cifreIntere <= 0) return 'cifre_intere_ignote'
  const massimo = dec('10').pow(cifreIntere)
  const vicinoAlFondoScala = precedente.gte(massimo.times(SOGLIA_FONDO_SCALA))
  return vicinoAlFondoScala && corrente.lt(massimo) ? 'giro_di_quadrante' : 'decrescente'
}

function validaLetture(dati: DatiPeriodo, c: ContatorePeriodo): Diagnostica[] {
  const { precedente, mesi } = mesiDelPeriodo(dati.periodo, dati.annoImposta)
  const attesi: readonly MeseDelPeriodo[] = c.kwhDiretti ? mesi : [precedente, ...mesi]
  const valoreDi = (m: MeseDelPeriodo): Dec | undefined =>
    dati.letture.find((l) => l.contatoreId === c.id && l.anno === m.anno && l.mese === m.mese)?.valore

  const out: Diagnostica[] = []
  const mancanti = attesi.filter((m) => valoreDi(m) === undefined)
  if (mancanti.length > 0) {
    const elenco = mancanti.map((m) => `${m.mese}/${m.anno}`).join(', ')
    out.push(bloccante('LETTURE_MANCANTI', `mancano le letture di ${elenco}`, { contatoreId: c.id }))
  }

  let prec: Dec | undefined
  for (const m of attesi) {
    const v = valoreDi(m)
    if (v === undefined) {
      prec = undefined
      continue
    }
    if (v.isNegative()) {
      out.push(bloccante('CONSUMO_NEGATIVO', 'lettura negativa', { contatoreId: c.id, mese: m.mese }))
    }
    if (!c.kwhDiretti && c.cifreIntere > 0 && v.gte(dec('10').pow(c.cifreIntere))) {
      out.push(bloccante('LETTURA_OLTRE_CIFRE', `lettura ${v.toString()} oltre la capacita di ${c.cifreIntere} cifre intere`, { contatoreId: c.id, mese: m.mese }))
    }
    if (!c.kwhDiretti && prec !== undefined && v.lt(prec)) {
      const esito = classificaCalo(prec, v, c.cifreIntere)
      if (esito === 'cifre_intere_ignote') {
        out.push(
          bloccante(
            'CIFRE_INTERE_MANCANTI',
            'lettura decrescente e numero di cifre intere non specificato: il giro di quadrante non e riconoscibile',
            { contatoreId: c.id, mese: m.mese },
          ),
        )
      }
      if (esito === 'decrescente') {
        out.push(
          bloccante('LETTURA_DECRESCENTE', `lettura decrescente: da ${prec.toString()} a ${v.toString()}`, {
            contatoreId: c.id,
            mese: m.mese,
          }),
        )
      }
    }
    prec = v
  }
  return out
}

/** Controlli sui dati del periodo; quelli di stato (dichiarazione precedente) restano nel database. */
export function validaPeriodo(dati: DatiPeriodo, regole: RegolePeriodo): Diagnostica[] {
  const out = dati.contatori.filter((c) => c.codice !== 'altri').flatMap((c) => validaLetture(dati, c))
  const conCodice = (codice: CodiceContatore) => dati.contatori.filter((c) => c.codice === codice)

  for (const k of regole.controlli) {
    switch (k.tipo) {
      case 'contatore_richiede_tipologia':
        for (const c of conCodice(k.contatore)) {
          if (c.tipologiaConsumo === undefined) {
            out.push(bloccante(k.codice, `tipologia di consumo mancante sul contatore ${c.matricola}`, { contatoreId: c.id }))
          }
        }
        break
      case 'contatore_richiede_scambio':
        for (const c of conCodice(k.contatore)) {
          if (c.tipoScambio === undefined || c.idControparte === undefined) {
            out.push(
              bloccante(k.codice, `tipo di scambio o controparte mancanti sul contatore ${c.matricola}`, {
                contatoreId: c.id,
              }),
            )
          }
        }
        break
      case 'debito_inizio_vietato':
        if (dati.quadroS.debitoInizio?.gt(0) === true) {
          out.push(bloccante(k.codice, 'il debito di inizio periodo non va compilato in questo periodo'))
        }
        break
      case 'codice_ditta_pattern':
        break
    }
  }
  return out
}

export function validaIntestazione(intestazione: Intestazione, regole: RegolePeriodo): Diagnostica[] {
  return regole.controlli.flatMap((k) =>
    k.tipo === 'codice_ditta_pattern' && !new RegExp(k.pattern).test(intestazione.codiceDitta)
      ? [bloccante(k.codice, `codice ditta ${intestazione.codiceDitta} non conforme al tracciato del periodo`)]
      : [],
  )
}
